import React, { useContext, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDispatch, useSelector } from 'react-redux';
import EditorContext from '../../../../contexts/EditorContext';
import { updateEditStatus } from '../../../../redux/actions/contractAction';
import {
  findRange,
  expandRangeToBlockBoundaries,
  htmlContainsBlocks,
} from '../../DocumentEditor/utils/textRangeMatcher';

/**
 * Apply a single AI-suggested edit to the TipTap editor.
 *
 * Strategy:
 *   1. Find the search_text inside the live PM doc using a normalization-
 *      tolerant matcher (handles smart quotes, NBSP, accents, minor LLM
 *      drift, capitalization). See textRangeMatcher.js for details.
 *   2. If the replacement HTML contains block-level tags (h2, p, ul...) and
 *      the matched range is not already block-aligned, expand the range to
 *      enclosing block boundaries before deletion. This is critical: if we
 *      try to insertContentAt a mid-paragraph position with HTML containing
 *      <h2>, TipTap will inline the heading inside the existing paragraph,
 *      destroying the structure.
 *   3. Use editor.commands.insertContentAt({from, to}, html, opts) which
 *      atomically deletes the range and parses the HTML through the editor
 *      schema, producing proper Heading/Paragraph nodes.
 *
 * Returns true on success, false if the text was not located.
 */
function applyEditToEditor(editor, edit) {
  const { state } = editor.view;
  const { doc } = state;

  const match = findRange(doc, edit.search_text);
  if (!match) return null;

  let { from, to } = match;
  const replacement = edit.replacement_text || '';
  const needsBlockInsert = htmlContainsBlocks(replacement);

  // For block-level replacements, snap to enclosing block boundaries so the
  // new heading/paragraph nodes flow at the top level instead of being
  // forced inline.
  if (needsBlockInsert && (edit.edit_type === 'replace' || edit.edit_type === 'insert_after')) {
    const expanded = expandRangeToBlockBoundaries(doc, from, to);
    if (expanded) {
      from = expanded.from;
      to = expanded.to;
    }
  }

  const sizeBefore = editor.state.doc.content.size;

  switch (edit.edit_type) {
    case 'replace': {
      editor.chain().focus()
        .insertContentAt({ from, to }, replacement, {
          parseOptions: { preserveWhitespace: 'full' },
          updateSelection: false,
        })
        .run();
      const delta = editor.state.doc.content.size - sizeBefore;
      return { from, to: to + delta };
    }

    case 'insert_after': {
      editor.chain().focus()
        .insertContentAt(to, replacement, {
          parseOptions: { preserveWhitespace: 'full' },
          updateSelection: false,
        })
        .run();
      const delta = editor.state.doc.content.size - sizeBefore;
      return { from: to, to: to + delta };
    }

    case 'delete':
      editor.chain().focus()
        .deleteRange({ from, to })
        .run();
      return { from, to: from };

    default:
      return null;
  }
}

/**
 * Scroll the editor to the given range and briefly highlight it so the user
 * sees exactly where the edit landed. Highlight fades out after 2s.
 *
 * Implementation note: we CANNOT add a CSS class directly to ProseMirror-
 * managed block elements — PM's docView reconciliation can strip arbitrary
 * className mutations on the next render cycle. Instead, we create absolutely-
 * positioned overlay divs appended to document.body, anchored to each block's
 * bounding rect, and reposition them on scroll. PM never touches elements
 * outside its own DOM tree.
 */
function flashRange(editor, from, to) {
  const view = editor?.view;
  if (!view) return;

  const docSize = view.state.doc.content.size;
  const safeFrom = Math.max(0, Math.min(from, docSize));
  const safeTo = Math.max(safeFrom, Math.min(to, docSize));

  const blockOf = (node) => {
    let n = node && node.nodeType === 3 ? node.parentElement : node;
    while (n && n !== view.dom && n !== document.body) {
      const display = window.getComputedStyle(n).display;
      if (
        display === 'block' ||
        display === 'flex' ||
        display === 'grid' ||
        display === 'list-item' ||
        display === 'table' ||
        display === 'table-cell'
      ) {
        return n;
      }
      n = n.parentElement;
    }
    return null;
  };

  // Always step one position INTO the content. At a node boundary,
  // domAtPos(boundary) is ambiguous; domAtPos(boundary + 1) is unambiguously
  // inside the first child of the new block.
  const resolveBlock = (pos) => {
    try {
      const inside = Math.min(Math.max(pos + 1, 1), docSize);
      const { node } = view.domAtPos(inside);
      return blockOf(node);
    } catch {
      return null;
    }
  };

  const startBlock = resolveBlock(safeFrom);
  if (!startBlock) return;

  const blocks = [startBlock];

  if (safeTo > safeFrom + 2) {
    const endBlock = resolveBlock(Math.max(safeFrom, safeTo - 2));
    if (endBlock && endBlock !== startBlock) {
      let curr = startBlock.nextElementSibling;
      let safety = 200;
      while (curr && safety-- > 0) {
        if (!blocks.includes(curr)) blocks.push(curr);
        if (curr === endBlock) break;
        curr = curr.nextElementSibling;
      }
      if (!blocks.includes(endBlock)) blocks.push(endBlock);
    }
  }

  // Find the actual scrolling container (page-mode editors have nested
  // scrollables; Element.scrollIntoView() can pick the wrong one). We use it
  // as both the scroll target AND the parent for the overlay divs, so the
  // overlay is naturally clipped inside the editor and can never bleed over
  // the toolbar.
  const TOP_OFFSET = 96; // clearance for the editor toolbar
  const findScrollContainer = (el) => {
    let n = el?.parentElement;
    while (n && n !== document.documentElement && n !== document.body) {
      const s = window.getComputedStyle(n);
      const overflowY = s.overflowY;
      if (
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        n.scrollHeight > n.clientHeight + 1
      ) {
        return n;
      }
      n = n.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const scrollContainer = findScrollContainer(startBlock);
  const isRootScroller =
    scrollContainer === document.scrollingElement ||
    scrollContainer === document.documentElement;

  // Ensure the container forms a containing block so absolute children are
  // positioned relative to it. Setting position:relative on a static element
  // is layout-safe (no offsets applied).
  if (!isRootScroller) {
    const pos = window.getComputedStyle(scrollContainer).position;
    if (pos === 'static') {
      scrollContainer.style.position = 'relative';
    }
  }

  const overlayParent = isRootScroller ? document.body : scrollContainer;

  // Build overlays as children of the scroll container, positioned absolutely
  // in its coordinate space. They scroll with content automatically — no
  // scroll listener needed.
  const overlays = blocks.map((b) => {
    const el = document.createElement('div');
    el.className = 'ai-edit-flash-overlay';
    el.style.position = 'absolute';
    el.style.pointerEvents = 'none';
    el.style.zIndex = '5';

    if (isRootScroller) {
      const r = b.getBoundingClientRect();
      el.style.position = 'fixed';
      el.style.top = `${r.top}px`;
      el.style.left = `${r.left}px`;
      el.style.width = `${r.width}px`;
      el.style.height = `${r.height}px`;
    } else {
      const containerRect = scrollContainer.getBoundingClientRect();
      const r = b.getBoundingClientRect();
      el.style.top = `${r.top - containerRect.top + scrollContainer.scrollTop}px`;
      el.style.left = `${r.left - containerRect.left + scrollContainer.scrollLeft}px`;
      el.style.width = `${r.width}px`;
      el.style.height = `${r.height}px`;
    }

    overlayParent.appendChild(el);
    return el;
  });

  // Defer the scroll by two animation frames so PM's internal autoscroll
  // (triggered by focus() in the apply chain) settles first — otherwise our
  // smooth scroll races with it and we land at the cursor's old position.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const containerRect = isRootScroller
        ? { top: 0 }
        : scrollContainer.getBoundingClientRect();
      const blockRect = startBlock.getBoundingClientRect();
      const targetTop =
        scrollContainer.scrollTop + (blockRect.top - containerRect.top) - TOP_OFFSET;
      scrollContainer.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    });
  });

  // Root-scroller fallback: overlays are position:fixed and need a scroll
  // listener to track the smooth scroll animation.
  let onScroll = null;
  if (isRootScroller) {
    onScroll = () => {
      blocks.forEach((b, i) => {
        const r = b.getBoundingClientRect();
        const o = overlays[i];
        o.style.top = `${r.top}px`;
        o.style.left = `${r.left}px`;
        o.style.width = `${r.width}px`;
        o.style.height = `${r.height}px`;
      });
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
  }

  setTimeout(() => {
    if (onScroll) {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    }
    overlays.forEach((o) => o.remove());
  }, 5100);
}

const EDIT_TYPE_CONFIG = {
  replace: { label: 'Replace', icon: EditIcon, color: 'info' },
  insert_after: { label: 'Insert', icon: AddIcon, color: 'success' },
  delete: { label: 'Delete', icon: DeleteOutlineIcon, color: 'error' },
};

// Truncation thresholds for the collapsed preview.
const SEARCH_PREVIEW_LIMIT = 120;
const REPLACEMENT_PREVIEW_LIMIT = 200;

// Detect whether a string looks like HTML (has at least one tag).
const HTML_TAG_RE = /<\/?[a-zA-Z][^>]*>/;
function looksLikeHtml(s) {
  return typeof s === 'string' && HTML_TAG_RE.test(s);
}

const DocumentEditSuggestion = ({ edit, messageId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const editorRef = useContext(EditorContext);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const contract = useSelector(state => state.contract.contract);
  const currentUser = useSelector(state => state.auth?.user?.user);
  const userRole = (contract?.collaborators || []).find(c => c.id === currentUser?.id)?.role;
  const isLocked = ['PENDING_SIGNATURE', 'SIGNED'].includes(contract?.status) || userRole === 'reviewer';

  const isPending = edit.status === 'pending';
  const isApplied = edit.status === 'applied';
  const isRejected = edit.status === 'rejected';

  const config = EDIT_TYPE_CONFIG[edit.edit_type] || EDIT_TYPE_CONFIG.replace;
  const EditTypeIcon = config.icon;

  // Decide whether the expand toggle is worth showing at all.
  const searchText = edit.search_text || '';
  const replacementText = edit.replacement_text || '';
  const replacementIsHtml = looksLikeHtml(replacementText);
  const isTruncatable =
    searchText.length > SEARCH_PREVIEW_LIMIT ||
    replacementText.length > REPLACEMENT_PREVIEW_LIMIT ||
    replacementIsHtml; // even short HTML benefits from rendered preview

  const renderSearch = () =>
    expanded || searchText.length <= SEARCH_PREVIEW_LIMIT
      ? searchText
      : searchText.slice(0, SEARCH_PREVIEW_LIMIT) + '...';

  const renderReplacementCollapsed = () =>
    replacementText.length > REPLACEMENT_PREVIEW_LIMIT
      ? replacementText.slice(0, REPLACEMENT_PREVIEW_LIMIT) + '...'
      : replacementText;

  const handleApply = () => {
    if (isLocked) return;
    const editor = editorRef?.current?.getEditor();
    if (!editor) {
      setError('Editor not available');
      return;
    }

    setError(null);
    try {
      const range = applyEditToEditor(editor, edit);
      if (range) {
        flashRange(editor, range.from, range.to);
        dispatch(updateEditStatus(messageId, edit.edit_id, 'applied'));
      } else {
        setError('Could not find the text in the document');
      }
    } catch (err) {
      console.error('Error applying edit:', err);
      setError('Failed to apply edit');
    }
  };

  const handleReject = () => {
    dispatch(updateEditStatus(messageId, edit.edit_id, 'rejected'));
  };

  // Determine border color based on status
  const getBorderColor = () => {
    if (isApplied) return theme.palette.success.main;
    if (isRejected) return theme.palette.text.disabled;
    return theme.palette.divider;
  };

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${getBorderColor()}`,
        borderRadius: '0 4px 4px 0',
        mb: 1,
        p: 1.25,
        bgcolor: isRejected
          ? alpha(theme.palette.action.disabledBackground, 0.3)
          : alpha(theme.palette.background.paper, 0.6),
        opacity: isRejected ? 0.6 : 1,
        transition: 'all 0.2s ease',
      }}
    >
      {/* Header: edit type chip + status */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Chip
          icon={<EditTypeIcon sx={{ fontSize: 14 }} />}
          label={config.label}
          size="small"
          color={config.color}
          variant="outlined"
          sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 }, '& .MuiChip-icon': { ml: 0.5 } }}
        />
        {isApplied && (
          <Chip
            icon={<CheckIcon sx={{ fontSize: 12 }} />}
            label="Applied"
            size="small"
            color="success"
            sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }}
          />
        )}
        {isRejected && (
          <Chip
            label="Rejected"
            size="small"
            sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 }, bgcolor: theme.palette.action.disabledBackground }}
          />
        )}
      </Box>

      {/* Explanation */}
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.8rem',
          color: theme.palette.text.secondary,
          mb: 0.75,
          lineHeight: 1.4,
          textAlign: 'left',
        }}
      >
        {edit.explanation}
      </Typography>

      {/* Diff preview (replace / insert_after) */}
      {edit.edit_type !== 'delete' && replacementText && (
        <Box
          sx={{
            bgcolor: alpha(theme.palette.common.black, 0.03),
            borderRadius: 1,
            p: 0.75,
            mb: 0.75,
            fontSize: '0.78rem',
            lineHeight: 1.5,
            textAlign: 'left',
          }}
        >
          {/* Old text (for replace) */}
          {edit.edit_type === 'replace' && searchText && (
            <Box
              sx={{
                textDecoration: 'line-through',
                color: theme.palette.error.main,
                opacity: 0.7,
                mb: 0.5,
                wordBreak: 'break-word',
                whiteSpace: expanded ? 'pre-wrap' : 'normal',
                maxHeight: expanded ? 280 : 'none',
                overflowY: expanded ? 'auto' : 'visible',
              }}
            >
              {renderSearch()}
            </Box>
          )}
          {/* New text — render as HTML preview when expanded, raw otherwise */}
          {expanded && replacementIsHtml ? (
            <Box
              sx={{
                color: theme.palette.success.dark,
                wordBreak: 'break-word',
                maxHeight: 320,
                overflowY: 'auto',
                pl: 0.5,
                borderLeft: `2px solid ${alpha(theme.palette.success.main, 0.4)}`,
                // Tame headings/paragraphs so the preview stays compact
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  margin: '4px 0',
                  color: theme.palette.success.dark,
                },
                '& p': { margin: '4px 0' },
                '& ul, & ol': { margin: '4px 0', paddingLeft: '18px' },
                '& strong': { fontWeight: 700 },
                '& em': { fontStyle: 'italic' },
                '& a': { color: theme.palette.primary.main, textDecoration: 'underline' },
                '& table': { borderCollapse: 'collapse', my: 0.5 },
                '& td, & th': { border: `1px solid ${theme.palette.divider}`, padding: '2px 6px' },
              }}
              dangerouslySetInnerHTML={{ __html: replacementText }}
            />
          ) : (
            <Box
              sx={{
                color: theme.palette.success.dark,
                fontWeight: 500,
                wordBreak: 'break-word',
                whiteSpace: expanded ? 'pre-wrap' : 'normal',
                fontSize: '0.78rem',
                maxHeight: expanded ? 320 : 'none',
                overflowY: expanded ? 'auto' : 'visible',
              }}
            >
              {expanded ? replacementText : renderReplacementCollapsed()}
            </Box>
          )}
        </Box>
      )}

      {/* Delete preview */}
      {edit.edit_type === 'delete' && searchText && (
        <Box
          sx={{
            bgcolor: alpha(theme.palette.error.main, 0.05),
            borderRadius: 1,
            p: 0.75,
            mb: 0.75,
            fontSize: '0.78rem',
            textDecoration: 'line-through',
            color: theme.palette.error.main,
            opacity: 0.7,
            lineHeight: 1.5,
            textAlign: 'left',
            wordBreak: 'break-word',
            whiteSpace: expanded ? 'pre-wrap' : 'normal',
            maxHeight: expanded ? 280 : 'none',
            overflowY: expanded ? 'auto' : 'visible',
          }}
        >
          {renderSearch()}
        </Box>
      )}

      {/* Expand / collapse toggle */}
      {isTruncatable && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: -0.25, mb: 0.5 }}>
          <Button
            size="small"
            onClick={() => setExpanded(v => !v)}
            endIcon={expanded
              ? <ExpandLessIcon sx={{ fontSize: 14 }} />
              : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 20,
              minWidth: 'auto',
              px: 0.75,
              fontSize: '0.65rem',
              textTransform: 'none',
              color: theme.palette.text.secondary,
              '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) },
            }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        </Box>
      )}

      {/* Error message */}
      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <ErrorOutlineIcon sx={{ fontSize: 14, color: theme.palette.warning.main, mr: 0.5 }} />
          <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Action buttons */}
      {isPending && (
        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
          <Tooltip title={isLocked ? (userRole === 'reviewer' ? 'Vous êtes en mode relecture' : 'Contract is locked for signature') : ''} disableHoverListener={!isLocked}>
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={handleApply}
                disabled={isLocked}
                startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 26,
                  fontSize: '0.72rem',
                  textTransform: 'none',
                  bgcolor: theme.palette.text.primary,
                  color: theme.palette.mode === 'dark' ? '#000' : '#fff',
                  '&:hover': { bgcolor: theme.palette.grey[800] },
                  px: 1.5,
                }}
              >
                Apply
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Dismiss this suggestion">
            <Button
              variant="outlined"
              size="small"
              onClick={handleReject}
              sx={{
                height: 26,
                fontSize: '0.72rem',
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
                borderColor: theme.palette.divider,
                color: theme.palette.text.secondary,
                '&:hover': {
                  borderColor: theme.palette.text.secondary,
                  bgcolor: alpha(theme.palette.action.hover, 0.5),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </Button>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default DocumentEditSuggestion;
